import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import {
  Plus,
  Download,
  Upload,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Edit,
  Trash2,
  Users,
  Info,
  Calendar,
  Check,
  ArrowLeft,
  Target,
  FileText,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  Search,
  X,
  SlidersHorizontal
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, PieChart, Pie, Cell, Legend, ComposedChart, Bar, Area, AreaChart } from 'recharts';
import Modal from '../components/Modal';
import ConfirmModal from '../components/ConfirmModal';
import FixedExpenseForm from '../components/FixedExpenseForm';
import FixedExpenseList from '../components/FixedExpenseList';
import PaymentAlerts from '../components/PaymentAlerts';
import { toast } from 'react-toastify';
import { formatCurrency } from '../utils/formatCurrency';

const Financial = () => {
  // console.log('Financial component re-rendered');
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartUpdating, setChartUpdating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showTithersModal, setShowTithersModal] = useState(false);
  const [showMatchesModal, setShowMatchesModal] = useState(false);
  const [showConfirmUnify, setShowConfirmUnify] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [tithers, setTithers] = useState([]);
  const [tithersArchived, setTithersArchived] = useState([]);
  const [tithersPurgeLoading, setTithersPurgeLoading] = useState(false);
  const [tithersPurgeResult, setTithersPurgeResult] = useState(null);
  const [matches, setMatches] = useState([]);
  const [matchesStats, setMatchesStats] = useState(null);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesDataset, setMatchesDataset] = useState('banco-cadastro');
  const [matchesThreshold, setMatchesThreshold] = useState(70);
  const [matchesReviewOnly, setMatchesReviewOnly] = useState(false);
  const [matchesLimit, setMatchesLimit] = useState(50);
  const [matchesPage, setMatchesPage] = useState(1);
  const [matchesPagination, setMatchesPagination] = useState({ total: 0, page: 1, limit: 50, totalPages: 1 });
  const [matchesPendingOnly, setMatchesPendingOnly] = useState(true);
  const [matchesPendingCount, setMatchesPendingCount] = useState(0);
  const [pendingUnify, setPendingUnify] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [importSuccess, setImportSuccess] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10
  });
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    category: '',
    month: '',
    year: new Date().getFullYear().toString()
  });
  const [formData, setFormData] = useState({
    type: 'entrada',
    amount: '',
    description: '',
    category: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    tithePayer: ''
  });
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    return tabParam === 'despesasFixas' ? 'despesasFixas' : 'geral';
  });
  const [hoveredTooltip, setHoveredTooltip] = useState(null);
  const [chartMode, setChartMode] = useState('completo'); // 'completo' | 'saldo'
  
  // Estados para despesas fixas
  const [fixedExpenses, setFixedExpenses] = useState([]);
  const [showFixedExpenseForm, setShowFixedExpenseForm] = useState(false);
  const [editingFixedExpense, setEditingFixedExpense] = useState(null);
  const [fixedExpensesLoading, setFixedExpensesLoading] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [showNotifications, setShowNotifications] = useState(true);

  // Meta de arrecadação
  const [monthlyGoal, setMonthlyGoal] = useState(() => {
    const s = localStorage.getItem('financial_monthly_goal');
    return s ? parseFloat(s) : 0;
  });
  const [showGoalInput, setShowGoalInput] = useState(false);
  const [goalInputValue, setGoalInputValue] = useState('');



  // Função para formatação de moeda

  // ── Helpers para dashboard ────────────────────────────────────────────────
  const currentMonthNum = new Date().getMonth() + 1;
  const prevMonthNum = currentMonthNum > 1 ? currentMonthNum - 1 : 12;

  const currentMonthSummary = React.useMemo(() =>
    summary?.monthlyData?.find(d => d._id === currentMonthNum) || { entradas: 0, saidas: 0 },
    [summary, currentMonthNum]
  );
  const prevMonthSummary = React.useMemo(() =>
    summary?.monthlyData?.find(d => d._id === prevMonthNum) || { entradas: 0, saidas: 0 },
    [summary, prevMonthNum]
  );

  const getTrend = (curr, prev) => {
    if (!prev || prev === 0) return null;
    const diff = curr - prev;
    const pct = Math.abs((diff / prev) * 100).toFixed(1);
    return { up: diff >= 0, pct, diff };
  };

  const getProjectedBalance = React.useCallback(() => {
    const saldoAtual = (currentMonthSummary.entradas || 0) - (currentMonthSummary.saidas || 0);
    const now = new Date();
    const pendingFixed = fixedExpenses
      .filter(e => e.isActive)
      .filter(e => !e.paymentHistory?.some(p => p.month === now.getMonth() + 1 && p.year === now.getFullYear()))
      .reduce((sum, e) => sum + (e.amount || 0), 0);
    return { saldoAtual, pendingFixed, projetado: saldoAtual - pendingFixed };
  }, [currentMonthSummary, fixedExpenses]);

  const getCategoryBars = React.useCallback(() => {
    const detailedData = summary?.monthlyDetailedData?.find(d => d._id === currentMonthNum);
    if (!detailedData?.categories) return [];
    return detailedData.categories
      .filter(c => c.type === 'entrada')
      .map(c => ({ name: c.category, value: c.total }))
      .sort((a, b) => b.value - a.value);
  }, [summary, currentMonthNum]);

  const saveGoal = () => {
    const val = parseFloat((goalInputValue || '').replace(',', '.'));
    if (!isNaN(val) && val >= 0) {
      setMonthlyGoal(val);
      localStorage.setItem('financial_monthly_goal', val.toString());
      toast.success('Meta atualizada!');
    }
    setShowGoalInput(false);
  };

  const exportReport = () => {
    const monthName = format(new Date(new Date().getFullYear(), currentMonthNum - 1, 1), 'MMMM yyyy', { locale: ptBR });
    const entradas = currentMonthSummary.entradas || 0;
    const saidas = currentMonthSummary.saidas || 0;
    const saldo = entradas - saidas;
    const proj = getProjectedBalance();
    const cats = getCategoryBars();
    const catsHtml = cats.map(c =>
      `<tr><td style="padding:6px 12px">${c.name}</td><td style="padding:6px 12px;text-align:right;font-weight:600;color:#16a34a">${formatCurrency(c.value)}</td></tr>`
    ).join('');
    const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"><title>Relatório Financeiro — ${monthName}</title>
<style>body{font-family:Arial,sans-serif;color:#1e293b;padding:32px;max-width:700px;margin:auto}
h1{font-size:22px;margin-bottom:4px}p.sub{color:#64748b;font-size:13px;margin:0 0 24px}
table{width:100%;border-collapse:collapse;margin-bottom:20px}
th{background:#f1f5f9;text-align:left;padding:8px 12px;font-size:13px}
td{border-top:1px solid #e2e8f0;font-size:14px}
.grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px;margin-bottom:20px}
.card{background:#f8fafc;border-radius:8px;padding:16px;border:1px solid #e2e8f0}
.card .label{font-size:12px;color:#64748b;margin-bottom:4px}
.card .val{font-size:20px;font-weight:700}
.green{color:#16a34a}.red{color:#dc2626}.blue{color:#2563eb}
@media print{body{padding:0}}</style></head>
<body>
<h1>Relatório Financeiro</h1><p class="sub">${monthName.charAt(0).toUpperCase() + monthName.slice(1)} · Gerado em ${format(new Date(), "dd/MM/yyyy 'às' HH:mm")}</p>
<div class="grid">
  <div class="card"><div class="label">Entradas do mês</div><div class="val green">${formatCurrency(entradas)}</div></div>
  <div class="card"><div class="label">Saídas do mês</div><div class="val red">${formatCurrency(saidas)}</div></div>
  <div class="card"><div class="label">Saldo do mês</div><div class="val ${saldo >= 0 ? 'green' : 'red'}">${formatCurrency(saldo)}</div></div>
</div>
<div class="grid">
  <div class="card"><div class="label">Saldo atual</div><div class="val ${proj.saldoAtual >= 0 ? 'green' : 'red'}">${formatCurrency(proj.saldoAtual)}</div></div>
  <div class="card"><div class="label">Despesas fixas pendentes</div><div class="val red">- ${formatCurrency(proj.pendingFixed)}</div></div>
  <div class="card"><div class="label">Saldo projetado</div><div class="val ${proj.projetado >= 0 ? 'green' : 'red'}">${formatCurrency(proj.projetado)}</div></div>
</div>
${cats.length > 0 ? `<h3 style="margin:0 0 8px;font-size:15px">Entradas por categoria</h3>
<table><thead><tr><th>Categoria</th><th style="text-align:right">Valor</th></tr></thead><tbody>${catsHtml}</tbody></table>` : ''}
${monthlyGoal > 0 ? `<p style="font-size:13px;color:#64748b">Meta de arrecadação: <strong>${formatCurrency(monthlyGoal)}</strong> · Atingido: <strong>${formatCurrency(entradas)}</strong> (${((entradas / monthlyGoal) * 100).toFixed(1)}%)</p>` : ''}
</body></html>`;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Permita pop-ups para exportar o relatório'); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 600);
  };

  const categories = {
    entrada: ['Dízimos', 'Ofertas', 'Doações', 'Eventos', 'Outros'],
    saida: ['Manutenção', 'Utilities', 'Salários', 'Eventos', 'Materiais', 'Outros']
  };

  const loadFinancialData = React.useCallback(async (page = 1, customFilters = null) => {
    try {
      setLoading(true);
      const filtersToUse = customFilters || filters;
      const params = {
        page,
        limit: pagination.limit,
        ...filtersToUse
      };
      
      // Fazer duas chamadas: uma para transações e outra para summary
      const summaryParams = filtersToUse.year ? { year: filtersToUse.year } : {};
      const [transactionsResponse, summaryResponse] = await Promise.all([
        api.get('/financial', { params }),
        api.get('/financial/summary', { params: summaryParams })
      ]);
      
      setTransactions(transactionsResponse.data.transactions || []);
      setSummary(summaryResponse.data || {});
      setPagination(transactionsResponse.data.pagination || {
        currentPage: 1,
        totalPages: 1,
        total: 0,
        limit: 10
      });
    } catch (error) {
      console.error('Erro ao carregar dados financeiros:', error);
      // Reset state on error to prevent crashes
      setTransactions([]);
      setSummary({});
      setPagination({
        currentPage: 1,
        totalPages: 1,
        total: 0,
        limit: 10
      });
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters]);

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => String(currentYear - i));

  const handleYearSelect = async (newYear) => {
    const newFilters = { ...filters, year: newYear };
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    setChartUpdating(true);
    try {
      await loadFinancialData(1, newFilters);
    } finally {
      setChartUpdating(false);
    }
  };

  

  const loadMatches = async () => {
    try {
      setMatchesLoading(true);
      const url = matchesDataset === 'banco-banco' ? '/api/matching/banco-banco' : '/api/matching/banco-cadastro';
      const res = await api.get(url.replace('/api',''), { params: { threshold: matchesThreshold, limit: matchesLimit, page: matchesPage, pendingOnly: matchesPendingOnly } });
      const all = res.data?.results || [];
      const filtered = matchesReviewOnly ? all.filter(r => r.reviewRecommended) : all;
      filtered.sort((a, b) => (b.finalScore || 0) - (a.finalScore || 0));
      setMatches(filtered);
      setMatchesStats(res.data?.stats || null);
      setMatchesPagination(res.data?.pagination || { total: filtered.length, page: matchesPage, limit: matchesLimit, totalPages: 1 });
    } catch (e) {
      setMatches([]);
      setMatchesStats(null);
      setMatchesPagination({ total: 0, page: 1, limit: matchesLimit, totalPages: 1 });
    } finally {
      setMatchesLoading(false);
    }
  };

  const loadMatchesCount = async () => {
    try {
      const res = await api.get('/matching/count', { params: { threshold: matchesThreshold } });
      setMatchesPendingCount(res.data?.total || 0);
    } catch (e) {
      // falha silenciosa — badge simplesmente não aparece
    }
  };

  const ignoreMatch = async (item) => {
    try {
      await api.post('/matching/ignore', {
        nameA: item.dbName,
        nameB: item.inputName,
        dataset: matchesDataset
      });
      toast.success('Match rejeitado');
      await Promise.all([loadMatches(), loadMatchesCount()]);
    } catch (e) {
      toast.error('Falha ao rejeitar match');
    }
  };

  const unifyMatch = async (item) => {
    try {
      const lenA = (item.normalized?.tokensA?.length || 0);
      const lenB = (item.normalized?.tokensB?.length || 0);
      const canonical = matchesDataset === 'banco-banco'
        ? (lenA >= lenB ? item.dbName : item.inputName)
        : item.inputName;
      const others = matchesDataset === 'banco-banco'
        ? [lenA >= lenB ? item.inputName : item.dbName]
        : [item.dbName];
      setMatchesLoading(true);
      const res = await api.post('/matching/unify', { fromNames: others, toName: canonical });
      const modified = res.data?.modified ?? 0;
      if (modified > 0) {
        toast.success(`Unificação concluída: ${modified} registro(s) atualizados`);
      } else {
        toast.info('Nenhum registro foi modificado — verifique variações de nome');
      }
      await loadMatches();
      await Promise.all([loadTithers(), loadMatchesCount()]);
    } catch (e) {
      const msg = e?.response?.data?.message || 'Falha ao unificar nomes';
      toast.error(msg);
    } finally {
      setMatchesLoading(false);
    }
  };

  const getUnifyPlan = (item) => {
    if (!item) return { fromNames: [], toName: '' };
    const lenA = (item.normalized?.tokensA?.length || 0);
    const lenB = (item.normalized?.tokensB?.length || 0);
    const canonical = matchesDataset === 'banco-banco'
      ? (lenA >= lenB ? item.dbName : item.inputName)
      : item.inputName;
    const others = matchesDataset === 'banco-banco'
      ? [lenA >= lenB ? item.inputName : item.dbName]
      : [item.dbName];
    return { fromNames: others, toName: canonical };
  };

  useEffect(() => {
    loadFinancialData();
    if (canViewTithers) loadMatchesCount();
  }, [loadFinancialData]);

  // Preparar dados para o gráfico
  const prepareChartData = () => {
    const monthNames = [
      'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
      'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
    ];
    
    // Criar array com todos os 12 meses
    const allMonths = [];
    
    for (let i = 1; i <= 12; i++) {
      // Procurar dados do mês no monthlyData
      const monthData = summary?.monthlyData?.find(month => month._id === i);
      
      // Garantir que os valores sejam números válidos
      const entradas = monthData && typeof monthData.entradas === 'number' && !isNaN(monthData.entradas) ? monthData.entradas : 0;
      const saidas = monthData && typeof monthData.saidas === 'number' && !isNaN(monthData.saidas) ? monthData.saidas : 0;
      const saldo = entradas - saidas;
      
      allMonths.push({
        month: monthNames[i - 1],
        entradas: entradas,
        saidas: saidas,
        saldo: saldo,
        saldoFormatted: formatCurrency(saldo)
      });
    }
    
    return allMonths;
  };

  // Preparar dados para o grid mensal com categorias detalhadas
  // Função para calcular resumo das transações filtradas
  const calculateFilteredSummary = () => {
    if (!transactions || transactions.length === 0) {
      return { entradas: 0, saidas: 0, saldo: 0 };
    }
    
    const entradas = transactions
      .filter(t => t.type === 'entrada')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const saidas = transactions
      .filter(t => t.type === 'saida')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const saldo = entradas - saidas;
    
    return { entradas, saidas, saldo };
  };

  // Função para verificar se há filtros aplicados
  const hasFiltersApplied = () => {
    return filters.search || filters.type || filters.category || filters.month;
  };

  const prepareMonthlyGridData = () => {

    const months = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    
    const gridData = months.map((monthName, index) => {
      const monthNumber = index + 1;
      const monthData = summary?.monthlyData?.find(m => m._id === monthNumber) || { entradas: 0, saidas: 0 };
      const detailedData = summary?.monthlyDetailedData?.find(m => m._id === monthNumber);
      
      // Calcular valores reais por categoria
      let oferta = 0;
      let dizimo = 0;
      let outros = 0;
      
      if (detailedData && detailedData.categories) {
        detailedData.categories.forEach(cat => {
          if (cat.type === 'entrada') {
            if (cat.category === 'Ofertas') {
              oferta += cat.total;
            } else if (cat.category === 'Dízimos') {
              dizimo += cat.total;
            } else {
              outros += cat.total;
            }
          }
        });
      }
      
      const totalEntradas = monthData.entradas || 0;
      const totalSaidas = monthData.saidas || 0;
      const despesas = totalSaidas; // Todas as saídas como despesas
      const saldo = totalEntradas - totalSaidas;
      
      return {
        monthName,
        monthNumber: monthNumber,
        oferta,
        dizimo,
        outros,
        despesas,
        saldo
      };
    });
    
    return gridData;
  };

  const chartData = summary?.monthlyData ? prepareChartData() : [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Corrigir problema de timezone na data
      // Quando o input type="date" envia '2024-09-26', o JavaScript interpreta como UTC
      // Isso causa um shift de timezone que faz a data aparecer como dia anterior
      // Solução: criar a data com horário local explícito
      const dateValue = formData.date;
      const correctedDate = new Date(dateValue + 'T12:00:00'); // Adiciona horário do meio-dia local
      
      const transactionData = {
        ...formData,
        amount: parseFloat(formData.amount.replace(',', '.')),
        date: correctedDate.toISOString() // Converte para ISO string
      };

      if (editingTransaction) {
        await api.put(`/financial/${editingTransaction._id}`, transactionData);
      } else {
        await api.post('/financial', transactionData);
      }
      
      setShowModal(false);
      resetForm();
      loadFinancialData(pagination.currentPage, filters);
    } catch (error) {
      console.error('Erro ao salvar transação:', error);
      console.error('Erro ao salvar transação. Verifique os dados e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setFormData({
      type: transaction.type,
      amount: transaction.amount.toString().replace('.', ','),
      description: transaction.description || '',
      category: transaction.category,
      date: format(new Date(transaction.date), 'yyyy-MM-dd'),
      tithePayer: transaction.tithePayer || ''
    });
    setShowModal(true);
  };

  const handleDelete = (transaction) => {
    setTransactionToDelete(transaction);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (transactionToDelete) {
      try {
        await api.delete(`/financial/${transactionToDelete._id}`);
        loadFinancialData(pagination.currentPage, filters);
        setShowDeleteModal(false);
        setTransactionToDelete(null);
      } catch (error) {
        console.error('Erro ao excluir transação:', error);
        console.error('Erro ao excluir transação.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'entrada',
      amount: '',
      description: '',
      category: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      tithePayer: ''
    });
    setEditingTransaction(null);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
    loadFinancialData(newPage, filters);
  };

  // Debounce para filtros
  const debounceTimeout = React.useRef(null);
  
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Reset para primeira página quando filtrar
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    
    // Debounce para evitar muitas requisições
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    
    debounceTimeout.current = setTimeout(() => {
      loadFinancialData(1, newFilters);
    }, 500);
  };

  const renderPagination = () => {
    if (!pagination || pagination.total === 0) {
      return null;
    }
    
    const maxVisiblePages = 5;
    let startPage = Math.max(1, (pagination.currentPage || 1) - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    return (
      <div className="card">
        <div className="card-body">
          <div className="flex items-center justify-between">
            <div className="text-sm text-secondary">
              Mostrando {(((pagination.currentPage || 1) - 1) * (pagination.limit || 10)) + 1} a {Math.min((pagination.currentPage || 1) * (pagination.limit || 10), pagination.total || 0)} de {pagination.total || 0} transações
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange((pagination.currentPage || 1) - 1)}
                disabled={(pagination.currentPage || 1) === 1}
                className="btn btn-sm btn-outline"
              >
                Anterior
              </button>
              
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNumber;
                  if (pagination.totalPages <= 5) {
                    pageNumber = i + 1;
                  } else if ((pagination.currentPage || 1) <= 3) {
                    pageNumber = i + 1;
                  } else if ((pagination.currentPage || 1) >= pagination.totalPages - 2) {
                    pageNumber = pagination.totalPages - 4 + i;
                  } else {
                    pageNumber = (pagination.currentPage || 1) - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      className={`btn btn-sm ${
                        pageNumber === (pagination.currentPage || 1)
                          ? 'btn-primary'
                          : 'btn-outline'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => handlePageChange((pagination.currentPage || 1) + 1)}
                disabled={(pagination.currentPage || 1) === pagination.totalPages}
                className="btn btn-sm btn-outline"
              >
                Próximo
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };



  const canEdit = user?.role === 'tesoureiro' || user?.role === 'administracao';
  const canViewTithers = user?.role === 'tesoureiro' || user?.role === 'pastor' || user?.role === 'administracao';

  const downloadTemplate = () => {
    const csvContent = `tipo,valor,descricao,categoria,data,dizimista\nentrada,100.00,Exemplo de entrada,Ofertas,01/01/2024,\nsaida,50.00,Exemplo de saída,Manutenção,01/01/2024,`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'modelo-transacoes.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCsvUpload = async (file) => {
    if (!file) return;
    setImportErrors([]);
    setImportSuccess(null);

    const formData = new FormData();
    formData.append('csvFile', file);

    try {
      setLoading(true);
      const res = await api.post('/financial/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const count = res.data?.count ?? 0;
      setImportSuccess(`${count} transaç${count === 1 ? 'ão importada' : 'ões importadas'} com sucesso!`);
      setCsvFile(null);
      loadFinancialData();
    } catch (error) {
      const data = error.response?.data;

      if (data?.errors?.length) {
        // Erros por linha vindos do backend
        setImportErrors(data.errors);
      } else if (data?.message) {
        setImportErrors([data.message]);
      } else if (error.response?.status === 403) {
        setImportErrors(['Você não tem permissão para importar transações.']);
      } else if (error.response?.status === 401) {
        setImportErrors(['Sessão expirada. Faça login novamente.']);
      } else if (!error.response) {
        setImportErrors(['Sem conexão com o servidor. Verifique sua internet e tente novamente.']);
      } else {
        setImportErrors(['Erro inesperado ao processar o arquivo. Tente novamente.']);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTithers = async () => {
    try {
      setLoading(true);
      const yearParam = filters.year || new Date().getFullYear();
      const response = await api.get(`/financial/tithers`, { params: { year: yearParam } });
      setTithers(response.data);
      setShowTithersModal(true);
      setTimeout(loadTithersArchive, 0);
    } catch (error) {
      console.error('Erro ao carregar dizimistas:', error);
      toast.error('Erro ao carregar lista de dizimistas.');
    } finally {
      setLoading(false);
    }
  };
  const loadTithersArchive = async () => {
    try {
      const res = await api.get('/financial/tithers/archive');
      setTithersArchived(res.data || []);
    } catch {}
  };
  const purgeInactiveTithers = async () => {
    try {
      setTithersPurgeLoading(true);
      const res = await api.post('/financial/tithers/purge-inactive');
      setTithersPurgeResult({ count: res.data?.archivedCount || 0 });
      await loadTithers();
      await loadTithersArchive();
      toast.success(`Removidos ${res.data?.archivedCount || 0} dizimistas inativos (3 meses).`);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Falha ao remover inativos');
    } finally {
      setTithersPurgeLoading(false);
    }
  };

  // Funções para despesas fixas
  const loadFixedExpenses = async () => {
    try {
      setFixedExpensesLoading(true);
      const response = await api.get('/fixed-expenses');
      setFixedExpenses(response.data);
    } catch (error) {
      console.error('Erro ao carregar despesas fixas:', error);
      if (error.response?.status === 403) {
        toast.error('Acesso negado. Apenas administradores e tesoureiros podem visualizar despesas fixas.');
      } else if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      } else {
        toast.error(`Erro ao carregar despesas fixas: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setFixedExpensesLoading(false);
    }
  };

  const handleFixedExpenseSubmit = async (expenseData) => {
    try {
      setFixedExpensesLoading(true);
      
      if (editingFixedExpense) {
        await api.put(`/fixed-expenses/${editingFixedExpense._id}`, expenseData);
      } else {
        await api.post('/fixed-expenses', expenseData);
      }
      
      setShowFixedExpenseForm(false);
      setEditingFixedExpense(null);
      loadFixedExpenses();
      toast.success(editingFixedExpense ? 'Despesa atualizada com sucesso!' : 'Despesa cadastrada com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar despesa fixa:', error);
      toast.error('Erro ao salvar despesa fixa.');
    } finally {
      setFixedExpensesLoading(false);
    }
  };

  const handleEditFixedExpense = (expense) => {
    setEditingFixedExpense(expense);
    setShowFixedExpenseForm(true);
  };

  const handleDeleteFixedExpense = async (expense) => {
    setExpenseToDelete(expense);
  };

  const confirmDeleteFixedExpense = async () => {
    if (!expenseToDelete) return;
    try {
      setFixedExpensesLoading(true);
      await api.delete(`/fixed-expenses/${expenseToDelete._id}`);
      loadFixedExpenses();
      toast.success('Despesa excluída com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir despesa fixa:', error);
      toast.error('Erro ao excluir despesa fixa.');
    } finally {
      setFixedExpensesLoading(false);
      setExpenseToDelete(null);
    }
  };

  const handleMarkFixedExpenseAsPaid = async (expense) => {
    try {
      setFixedExpensesLoading(true);
      await api.post(`/fixed-expenses/${expense._id}/pay`);
      loadFixedExpenses();
      // Disparar evento global para atualizar alertas imediatamente
      window.dispatchEvent(new Event('refreshPaymentAlerts'));
    } catch (error) {
      console.error('Erro ao marcar despesa como paga:', error);
      // Opcional: manter um feedback não intrusivo no futuro (toast), por enquanto remover alert bloqueante
    } finally {
      setFixedExpensesLoading(false);
    }
  };

  // Carregar despesas fixas quando a aba for ativada ou quando o componente for montado
  React.useEffect(() => {
    if (['administracao', 'tesoureiro', 'pastor'].includes(user?.role)) {
      loadFixedExpenses();
    }
  }, [user?.role]);

  // Carregar despesas fixas quando a aba for ativada
  React.useEffect(() => {
    if (activeTab === 'despesasFixas' && ['administracao', 'tesoureiro', 'pastor'].includes(user?.role)) {
      loadFixedExpenses();
    }
  }, [activeTab, user?.role]);

  const handleCloseFixedExpenseForm = () => {
    setShowFixedExpenseForm(false);
    setEditingFixedExpense(null);
  };

  const handleOpenFixedExpenseForm = () => {
    setShowFixedExpenseForm(true);
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 60%, #60a5fa 100%)',
        borderRadius: '16px',
        padding: '24px',
        color: '#ffffff',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Círculos decorativos */}
        <div style={{ position: 'absolute', width: '180px', height: '180px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', top: '-60px', right: '-40px' }} />
        <div style={{ position: 'absolute', width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', bottom: '-30px', right: '80px' }} />

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '52px', height: '52px', borderRadius: '14px',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, backdropFilter: 'blur(4px)',
              border: '2px solid rgba(255,255,255,0.3)',
            }}>
              <DollarSign size={24} color="#fff" />
            </div>
            <div>
              <p style={{ fontSize: '13px', opacity: '0.8', margin: '0 0 2px' }}>Gestão financeira</p>
              <h1 style={{ fontSize: '20px', fontWeight: '700', margin: '0 0 4px', lineHeight: '1.2' }}>Financeiro</h1>
              <span style={{
                display: 'inline-block', fontSize: '11px', fontWeight: '600',
                background: 'rgba(255,255,255,0.2)', borderRadius: '20px',
                padding: '2px 10px', backdropFilter: 'blur(4px)',
              }}>
                {format(new Date(), "MMMM 'de' yyyy", { locale: ptBR }).replace(/^\w/, c => c.toUpperCase())}
              </span>
            </div>
          </div>

          {canEdit && (
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={() => { resetForm(); setShowModal(true); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '8px 16px', borderRadius: '10px', fontSize: '14px', fontWeight: '600',
                  background: 'rgba(255,255,255,0.95)', color: '#1d4ed8',
                  border: 'none', cursor: 'pointer',
                }}
                title="Adicionar uma nova transação financeira"
              >
                <Plus size={16} />
                <span>Nova Transação</span>
              </button>
              <button
                onClick={() => setShowImportModal(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '8px 16px', borderRadius: '10px', fontSize: '14px', fontWeight: '600',
                  background: 'rgba(255,255,255,0.15)', color: '#fff',
                  border: '1.5px solid rgba(255,255,255,0.4)', cursor: 'pointer',
                  backdropFilter: 'blur(4px)',
                }}
                title="Importar transações de arquivo CSV"
              >
                <Upload size={16} />
                <span>Importar CSV</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Ações secundárias */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <button
          onClick={exportReport}
          className="btn btn-outline"
          title="Exportar relatório do mês atual"
        >
          <FileText className="w-4 h-4" />
          <span>Exportar</span>
        </button>
        {canViewTithers && (
          <button
            onClick={loadTithers}
            className="btn btn-outline"
            title="Visualizar lista completa dos dizimistas"
          >
            <Users className="w-4 h-4" />
            <span>Dizimistas</span>
          </button>
        )}
        {canViewTithers && (
          <button
            onClick={() => { setShowMatchesModal(true); loadMatches(); }}
            className="btn btn-outline"
            title="Ver possíveis correspondências de nomes"
            style={{ position: 'relative' }}
          >
            <Users className="w-4 h-4" />
            <span>Matches</span>
            {matchesPendingCount > 0 && (
              <span style={{
                position: 'absolute', top: '-6px', right: '-6px',
                background: '#ef4444', color: '#fff', borderRadius: '9999px',
                fontSize: '10px', fontWeight: 700, minWidth: '18px', height: '18px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0 4px', lineHeight: 1
              }}>
                {matchesPendingCount > 99 ? '99+' : matchesPendingCount}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Navigation Tabs */}
      <div style={{ background: '#f1f5f9', borderRadius: '12px', padding: '4px', display: 'flex', gap: '4px' }}>
        {[
          { id: 'geral', label: 'Geral', icon: DollarSign },
          { id: 'mensal', label: 'Mensal', icon: TrendingUp },
          { id: 'despesasFixas', label: 'Despesas Fixas', icon: Calendar },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              padding: '8px 12px', borderRadius: '9px', fontSize: '14px', fontWeight: '500',
              border: 'none', cursor: 'pointer', transition: 'all 0.2s',
              background: activeTab === id ? '#ffffff' : 'transparent',
              color: activeTab === id ? '#1d4ed8' : '#64748b',
              boxShadow: activeTab === id ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            <Icon size={16} />
            <span>{label}</span>
          </button>
        ))}
      </div>

      {/* Summary Cards ou Grid Mensal */}
      {summary && (
        <div>
          {activeTab === 'mensal' ? (
            // Grid Mensal responsivo - 4 cards por linha com design melhorado
            <div className="monthly-grid">
                {prepareMonthlyGridData().map((monthData) => (
                  <div key={monthData.monthNumber} className="group">
                    <div className="card hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border-l-4 border-l-blue-500">
                      <div className="card-body p-4">
                        <div className="text-center">
                          <h3 className="text-base font-bold text-gray-800 mb-4">
                            {monthData.monthName}
                          </h3>
                          <div className="space-y-3">
                            <div className="rounded-lg p-3">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-700">Oferta</span>
                                <span className="text-sm font-bold text-blue-600">
                                  {formatCurrency(monthData.oferta)}
                                </span>
                              </div>
                            </div>
                            <div className="rounded-lg p-3">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-700">Dízimo</span>
                                <span className="text-sm font-bold text-green-600">
                                  {formatCurrency(monthData.dizimo)}
                                </span>
                              </div>
                            </div>
                            <div className="rounded-lg p-3">
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-1 relative">
                                  <span className="text-sm font-medium text-gray-700">Outros</span>
                                  <Info 
                                    className="w-3 h-3 text-purple-400 hover:text-purple-600 cursor-help" 
                                    onMouseEnter={() => setHoveredTooltip(`outros-${monthData.monthNumber}`)}
                                    onMouseLeave={() => setHoveredTooltip(null)}
                                  />
                                  {hoveredTooltip === `outros-${monthData.monthNumber}` && (
                                    <div className="absolute left-0 top-full mt-1 bg-gray-900 text-white text-[10px] rounded-lg px-2 py-1.5 shadow-lg z-50 min-w-max max-w-48">
                                      <div className="font-semibold mb-1">Categoria: Outros</div>
                                      <div className="font-semibold mb-1">Valor: {formatCurrency(monthData.outros)}</div>
                                      <div className="text-gray-300 mb-1 text-[9px]">Esta categoria inclui:</div>
                                      <div className="text-[9px]">• Ofertas especiais</div>
                                      <div className="text-[9px]">• Doações diversas</div>
                                      <div className="text-[9px]">• Outras entradas não categorizadas</div>
                                      <div className="absolute -top-1 left-3 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                                    </div>
                                  )}
                                </div>
                                <span className="text-sm font-bold text-purple-600">
                                  {formatCurrency(monthData.outros)}
                                </span>
                              </div>
                            </div>
                            <div className="rounded-lg p-3">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-700">Despesas</span>
                                <span className="text-sm font-bold text-red-600">
                                  {formatCurrency(monthData.despesas)}
                                </span>
                              </div>
                            </div>
                            <div className={`rounded-lg p-3 border-2 ${
                              monthData.saldo >= 0 
                                ? 'bg-green-50 border-green-200' 
                                : 'bg-red-50 border-red-200'
                            }`}>
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-bold text-gray-700">Saldo Final</span>
                                <span className={`text-lg font-bold ${
                                  monthData.saldo >= 0 ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {formatCurrency(monthData.saldo)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          ) : activeTab === 'geral' ? (
            // Cards de resumo redesenhados
            <div className="space-y-4">
              {/* Linha 1: resumo anual */}
              <div className="stats-grid">
                {/* Entrada Anual */}
                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs md:text-sm text-secondary">Entrada Anual</p>
                      <div style={{ width: 36, height: 36, borderRadius: '10px', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <TrendingUp className="w-5 h-5" style={{ color: '#16a34a' }} />
                      </div>
                    </div>
                    <p className="text-lg md:text-xl font-bold text-success">{formatCurrency(summary.summary?.entradas || 0)}</p>
                  </div>
                </div>

                {/* Saída Anual */}
                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs md:text-sm text-secondary">Saída Anual</p>
                      <div style={{ width: 36, height: 36, borderRadius: '10px', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <TrendingDown className="w-5 h-5" style={{ color: '#dc2626' }} />
                      </div>
                    </div>
                    <p className="text-lg md:text-xl font-bold text-danger">{formatCurrency(summary.summary?.saidas || 0)}</p>
                  </div>
                </div>

                {/* Saldo Anual */}
                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs md:text-sm text-secondary">Saldo Anual</p>
                      <div style={{ width: 36, height: 36, borderRadius: '10px', background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <DollarSign className="w-5 h-5" style={{ color: '#2563eb' }} />
                      </div>
                    </div>
                    <p className={`text-lg md:text-xl font-bold ${(summary.summary?.saldo || 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                      {formatCurrency(summary.summary?.saldo || 0)}
                    </p>
                  </div>
                </div>

                {/* Entrada do Mês com tendência */}
                {(() => {
                  const trend = getTrend(currentMonthSummary.entradas, prevMonthSummary.entradas);
                  return (
                    <div className="card">
                      <div className="card-body">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs md:text-sm text-secondary">Entrada do Mês</p>
                          <div style={{ width: 36, height: 36, borderRadius: '10px', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <TrendingUp className="w-5 h-5" style={{ color: '#16a34a' }} />
                          </div>
                        </div>
                        <p className="text-lg md:text-xl font-bold text-success">{formatCurrency(currentMonthSummary.entradas)}</p>
                        {trend && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                            {trend.up ? <ArrowUpRight size={13} color="#16a34a" /> : <ArrowDownRight size={13} color="#dc2626" />}
                            <span style={{ fontSize: 11, color: trend.up ? '#16a34a' : '#dc2626', fontWeight: 600 }}>{trend.pct}% vs mês anterior</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Saída do Mês com tendência */}
                {(() => {
                  const trend = getTrend(currentMonthSummary.saidas, prevMonthSummary.saidas);
                  return (
                    <div className="card">
                      <div className="card-body">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs md:text-sm text-secondary">Saída do Mês</p>
                          <div style={{ width: 36, height: 36, borderRadius: '10px', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <TrendingDown className="w-5 h-5" style={{ color: '#dc2626' }} />
                          </div>
                        </div>
                        <p className="text-lg md:text-xl font-bold text-danger">{formatCurrency(currentMonthSummary.saidas)}</p>
                        {trend && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                            {trend.up ? <ArrowUpRight size={13} color="#dc2626" /> : <ArrowDownRight size={13} color="#16a34a" />}
                            <span style={{ fontSize: 11, color: trend.up ? '#dc2626' : '#16a34a', fontWeight: 600 }}>{trend.pct}% vs mês anterior</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Saldo do Mês */}
                {(() => {
                  const saldo = (currentMonthSummary.entradas || 0) - (currentMonthSummary.saidas || 0);
                  const prevSaldo = (prevMonthSummary.entradas || 0) - (prevMonthSummary.saidas || 0);
                  const trend = getTrend(saldo, prevSaldo);
                  return (
                    <div className="card">
                      <div className="card-body">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs md:text-sm text-secondary">Saldo do Mês</p>
                          <div style={{ width: 36, height: 36, borderRadius: '10px', background: saldo >= 0 ? '#dcfce7' : '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <DollarSign className="w-5 h-5" style={{ color: saldo >= 0 ? '#16a34a' : '#dc2626' }} />
                          </div>
                        </div>
                        <p className={`text-lg md:text-xl font-bold ${saldo >= 0 ? 'text-success' : 'text-danger'}`}>{formatCurrency(saldo)}</p>
                        {trend && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                            {trend.up ? <ArrowUpRight size={13} color="#16a34a" /> : <ArrowDownRight size={13} color="#dc2626" />}
                            <span style={{ fontSize: 11, color: trend.up ? '#16a34a' : '#dc2626', fontWeight: 600 }}>{trend.pct}% vs mês anterior</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Linha 2: Meta + Projeção (full-width) */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {/* Meta de Arrecadação */}
                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4" style={{ color: '#7c3aed' }} />
                        <p className="text-sm font-semibold text-primary">Meta de Arrecadação</p>
                      </div>
                      {canEdit && (
                        <button onClick={() => { setGoalInputValue(monthlyGoal > 0 ? monthlyGoal.toFixed(2).replace('.', ',') : ''); setShowGoalInput(v => !v); }}
                          style={{ fontSize: 12, color: '#7c3aed', background: '#f5f3ff', border: '1px solid #ede9fe', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontWeight: 600 }}>
                          {showGoalInput ? 'Cancelar' : monthlyGoal > 0 ? 'Editar' : 'Definir'}
                        </button>
                      )}
                    </div>
                    {showGoalInput ? (
                      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                        <input
                          type="text" inputMode="decimal"
                          value={goalInputValue} onChange={e => setGoalInputValue(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && saveGoal()}
                          placeholder="Ex: 5000,00"
                          style={{ flex: 1, padding: '6px 10px', border: '1px solid #ddd6fe', borderRadius: 7, fontSize: 14, outline: 'none' }}
                          autoFocus
                        />
                        <button onClick={saveGoal} style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 7, padding: '6px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                          Salvar
                        </button>
                      </div>
                    ) : null}
                    {monthlyGoal > 0 ? (() => {
                      const pct = Math.min((currentMonthSummary.entradas / monthlyGoal) * 100, 100);
                      const over = currentMonthSummary.entradas > monthlyGoal;
                      return (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                            <span style={{ fontSize: 13, color: '#64748b' }}>Arrecadado: <strong style={{ color: over ? '#16a34a' : '#1e293b' }}>{formatCurrency(currentMonthSummary.entradas)}</strong></span>
                            <span style={{ fontSize: 13, color: '#64748b' }}>Meta: <strong>{formatCurrency(monthlyGoal)}</strong></span>
                          </div>
                          <div style={{ background: '#f1f5f9', borderRadius: 8, height: 10, overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', borderRadius: 8, background: over ? '#16a34a' : pct >= 70 ? '#f59e0b' : '#7c3aed', transition: 'width 0.5s' }} />
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                            <span style={{ fontSize: 11, color: over ? '#16a34a' : '#7c3aed', fontWeight: 700 }}>{pct.toFixed(1)}% atingido</span>
                            {!over && <span style={{ fontSize: 11, color: '#94a3b8' }}>Faltam {formatCurrency(monthlyGoal - currentMonthSummary.entradas)}</span>}
                            {over && <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>Meta superada! 🎉</span>}
                          </div>
                        </>
                      );
                    })() : (
                      <p style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>Nenhuma meta definida para este mês.</p>
                    )}
                  </div>
                </div>

                {/* Fluxo de Caixa Projetado */}
                {(() => {
                  const proj = getProjectedBalance();
                  const isNegative = proj.projetado < 0;
                  return (
                    <div className="card">
                      <div className="card-body">
                        <div className="flex items-center gap-2 mb-3">
                          {isNegative ? <AlertTriangle className="w-4 h-4" style={{ color: '#dc2626' }} /> : <TrendingUp className="w-4 h-4" style={{ color: '#0891b2' }} />}
                          <p className="text-sm font-semibold text-primary">Projeção do Mês</p>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                          <div style={{ textAlign: 'center', background: '#f0fdf4', borderRadius: 8, padding: '10px 6px' }}>
                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Saldo atual</div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: proj.saldoAtual >= 0 ? '#16a34a' : '#dc2626' }}>{formatCurrency(proj.saldoAtual)}</div>
                          </div>
                          <div style={{ textAlign: 'center', background: '#fef2f2', borderRadius: 8, padding: '10px 6px' }}>
                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Fixas pendentes</div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#dc2626' }}>- {formatCurrency(proj.pendingFixed)}</div>
                          </div>
                          <div style={{ textAlign: 'center', background: isNegative ? '#fef2f2' : '#eff6ff', borderRadius: 8, padding: '10px 6px', border: `1px solid ${isNegative ? '#fecaca' : '#bfdbfe'}` }}>
                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Projetado</div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: isNegative ? '#dc2626' : '#1d4ed8' }}>{formatCurrency(proj.projetado)}</div>
                          </div>
                        </div>
                        {isNegative && (
                          <p style={{ fontSize: 11, color: '#dc2626', marginTop: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <AlertTriangle size={11} /> Projeção negativa — revise as despesas fixas pendentes.
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Linha 3: Distribuição por categoria */}
              {getCategoryBars().length > 0 && (
                <div className="card">
                  <div className="card-body">
                    <p className="text-sm font-semibold text-primary mb-3">Entradas por Categoria — {format(new Date(new Date().getFullYear(), currentMonthNum - 1, 1), 'MMMM', { locale: ptBR })}</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {(() => {
                        const bars = getCategoryBars();
                        const max = Math.max(...bars.map(b => b.value), 1);
                        const colors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];
                        return bars.map((b, i) => (
                          <div key={b.name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 12, color: '#64748b', width: 80, flexShrink: 0 }}>{b.name}</span>
                            <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 6, height: 10, overflow: 'hidden' }}>
                              <div style={{ width: `${(b.value / max) * 100}%`, height: '100%', borderRadius: 6, background: colors[i % colors.length], transition: 'width 0.4s' }} />
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', width: 90, textAlign: 'right', flexShrink: 0 }}>{formatCurrency(b.value)}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : activeTab === 'despesasFixas' ? (
            // Seção de Despesas Fixas
            user && ['administracao', 'tesoureiro'].includes(user.role) ? (
              <div className="space-y-6">
                {/* Alertas de Pagamento */}
                <PaymentAlerts
                  expenses={fixedExpenses}
                  onMarkAsPaid={handleMarkFixedExpenseAsPaid}
                  showNotifications={showNotifications}
                  onToggleNotifications={() => setShowNotifications(!showNotifications)}
                />
                
                {/* Lista de Despesas Fixas */}
                <FixedExpenseList
                  expenses={fixedExpenses}
                  onEdit={handleEditFixedExpense}
                  onDelete={handleDeleteFixedExpense}
                  onMarkAsPaid={handleMarkFixedExpenseAsPaid}
                  onAdd={handleOpenFixedExpenseForm}
                  loading={fixedExpensesLoading}
                />
              </div>
            ) : user && user.role === 'pastor' ? (
              <div className="space-y-6">
                <PaymentAlerts
                  expenses={fixedExpenses}
                  showNotifications={showNotifications}
                  onToggleNotifications={() => setShowNotifications(!showNotifications)}
                  readOnly
                />
                <FixedExpenseList
                  expenses={fixedExpenses}
                  loading={fixedExpensesLoading}
                  readOnly
                />
              </div>
            ) : (
              <div className="card">
                <div className="card-body text-center py-12">
                  <div className="text-gray-500 mb-4">
                    <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Acesso Restrito</h3>
                    <p className="text-gray-500">
                      Apenas administradores e tesoureiros podem acessar o gerenciamento de despesas fixas.
                    </p>
                  </div>
                </div>
              </div>
            )
          ) : null}
        </div>
      )}

      {/* Chart - apenas na aba geral */}
      {summary && activeTab === 'geral' && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Finanças Anual</h2>
            <div className="flex items-center gap-2 ml-auto">
              {/* Toggle modo */}
              <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 3, gap: 2 }}>
                {[{ key: 'completo', label: 'Completo' }, { key: 'saldo', label: 'Saldo' }].map(m => (
                  <button
                    key={m.key}
                    onClick={() => setChartMode(m.key)}
                    style={{
                      padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                      fontSize: 12, fontWeight: 600,
                      background: chartMode === m.key ? '#ffffff' : 'transparent',
                      color: chartMode === m.key ? '#1e293b' : '#94a3b8',
                      boxShadow: chartMode === m.key ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
                      transition: 'all 0.2s',
                    }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <select
                id="year-select"
                value={filters.year}
                onChange={(e) => handleYearSelect(e.target.value)}
                aria-busy={chartUpdating ? 'true' : 'false'}
                style={{
                  padding: '4px 8px', borderRadius: 6, border: '1px solid #e2e8f0',
                  background: '#f1f5f9', fontSize: 12, fontWeight: 600, color: '#1e293b',
                  cursor: 'pointer', outline: 'none', appearance: 'none',
                  WebkitAppearance: 'none', textAlign: 'center',
                }}
              >
                {yearOptions.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="card-body">
            {chartUpdating && (
              <div className="flex items-center gap-2 text-sm text-secondary mb-2" role="status" aria-live="polite">
                <div className="loading" />
                <span>Atualizando dados de {filters.year}...</span>
              </div>
            )}

            {/* Legenda manual */}
            <div style={{ display: 'flex', gap: 20, marginBottom: 16, flexWrap: 'wrap' }}>
              {chartMode === 'completo' ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 12, height: 12, borderRadius: 3, background: '#10b981' }} />
                    <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Entradas</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 12, height: 12, borderRadius: 3, background: '#ef4444' }} />
                    <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Saídas</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 20, height: 3, borderRadius: 2, background: '#3b82f6' }} />
                    <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Saldo</span>
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 20, height: 3, borderRadius: 2, background: '#3b82f6' }} />
                  <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Saldo mensal</span>
                </div>
              )}
            </div>

            <div style={{ width: '100%', height: 380 }}>
              <ResponsiveContainer width="100%" height="100%">
                {chartMode === 'completo' ? (
                  <ComposedChart
                    data={chartData}
                    margin={{ top: 10, right: 24, left: 8, bottom: 5 }}
                    barGap={4}
                  >
                    <defs>
                      <linearGradient id="gradEntradas" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0.65} />
                      </linearGradient>
                      <linearGradient id="gradSaidas" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ef4444" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#ef4444" stopOpacity={0.65} />
                      </linearGradient>
                      <linearGradient id="gradSaldo" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.2} />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" vertical={false} />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 12, fill: '#94a3b8', fontWeight: 500 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#94a3b8' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={v => `${(v / 1000).toFixed(0)}k`}
                      width={42}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(59,130,246,0.06)', radius: 6 }}
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const nameMap = { entradas: 'Entradas', saidas: 'Saídas', saldo: 'Saldo' };
                        const colorMap = { entradas: '#10b981', saidas: '#ef4444', saldo: '#3b82f6' };
                        return (
                          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 16px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', minWidth: 190 }}>
                            <p style={{ fontWeight: 700, color: '#1e293b', marginBottom: 10, fontSize: 13 }}>{label}</p>
                            {payload.map(entry => (
                              <div key={entry.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 5, alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: colorMap[entry.dataKey] || entry.color }} />
                                  <span style={{ fontSize: 12, color: '#64748b' }}>{nameMap[entry.dataKey] || entry.name}</span>
                                </div>
                                <span style={{ fontSize: 13, color: '#1e293b', fontWeight: 700 }}>{formatCurrency(entry.value)}</span>
                              </div>
                            ))}
                          </div>
                        );
                      }}
                    />
                    <ReferenceLine y={0} stroke="#cbd5e1" strokeDasharray="4 3" strokeWidth={1.5} />
                    <Bar
                      dataKey="entradas"
                      name="Entradas"
                      fill="url(#gradEntradas)"
                      radius={[5, 5, 0, 0]}
                      maxBarSize={28}
                      isAnimationActive={true}
                      animationDuration={900}
                      animationEasing="ease-out"
                      animationBegin={0}
                    />
                    <Bar
                      dataKey="saidas"
                      name="Saídas"
                      fill="url(#gradSaidas)"
                      radius={[5, 5, 0, 0]}
                      maxBarSize={28}
                      isAnimationActive={true}
                      animationDuration={900}
                      animationEasing="ease-out"
                      animationBegin={150}
                    />
                    <Line
                      type="monotone"
                      dataKey="saldo"
                      name="Saldo"
                      stroke="#3b82f6"
                      strokeWidth={3}
                      dot={(props) => {
                        const { cx, cy, payload } = props;
                        const v = payload?.saldo || 0;
                        const c = v > 0 ? '#10b981' : v < 0 ? '#ef4444' : '#3b82f6';
                        return <circle key={`dot-${cx}`} cx={cx} cy={cy} r={5} fill="#fff" stroke={c} strokeWidth={2.5} />;
                      }}
                      activeDot={{ r: 8, fill: '#3b82f6', stroke: '#fff', strokeWidth: 2 }}
                      isAnimationActive={true}
                      animationDuration={1400}
                      animationEasing="ease-out"
                      animationBegin={300}
                    />
                  </ComposedChart>
                ) : (
                  /* Modo Saldo — AreaChart */
                  <AreaChart
                    data={chartData}
                    margin={{ top: 10, right: 24, left: 8, bottom: 5 }}
                  >
                    <defs>
                      <linearGradient id="gradAreaPos" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" vertical={false} />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 12, fill: '#94a3b8', fontWeight: 500 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#94a3b8' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={v => `${(v / 1000).toFixed(0)}k`}
                      width={42}
                    />
                    <Tooltip
                      cursor={{ stroke: '#3b82f6', strokeWidth: 1.5, strokeDasharray: '4 2' }}
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const v = payload[0]?.value || 0;
                        return (
                          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 16px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
                            <p style={{ fontWeight: 700, color: '#1e293b', marginBottom: 6, fontSize: 13 }}>{label}</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ width: 8, height: 8, borderRadius: '50%', background: v >= 0 ? '#10b981' : '#ef4444' }} />
                              <span style={{ fontSize: 13, fontWeight: 700, color: v >= 0 ? '#10b981' : '#ef4444' }}>
                                {formatCurrency(v)}
                              </span>
                            </div>
                          </div>
                        );
                      }}
                    />
                    <ReferenceLine y={0} stroke="#cbd5e1" strokeDasharray="4 3" strokeWidth={1.5} />
                    <Area
                      type="monotone"
                      dataKey="saldo"
                      stroke="#3b82f6"
                      strokeWidth={3}
                      fill="url(#gradAreaPos)"
                      dot={(props) => {
                        const { cx, cy, payload } = props;
                        const v = payload?.saldo || 0;
                        const c = v > 0 ? '#10b981' : v < 0 ? '#ef4444' : '#3b82f6';
                        return <circle key={`dot-${cx}`} cx={cx} cy={cy} r={5} fill="#fff" stroke={c} strokeWidth={2.5} />;
                      }}
                      activeDot={{ r: 8, fill: '#3b82f6', stroke: '#fff', strokeWidth: 2 }}
                      isAnimationActive={true}
                      animationDuration={1400}
                      animationEasing="ease-out"
                    />
                  </AreaChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Filters - apenas na aba geral */}
      {activeTab === 'geral' && (
      <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <SlidersHorizontal size={16} style={{ color: '#64748b', flexShrink: 0 }} />

          {/* Search */}
          <div style={{ position: 'relative', flex: '1', minWidth: '180px' }}>
            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
            <input
              type="text"
              placeholder="Buscar descrição..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              style={{
                width: '100%', padding: '7px 10px 7px 30px', borderRadius: '8px',
                border: '1px solid #e2e8f0', fontSize: '13px', color: '#1e293b',
                background: '#f8fafc', outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Tipo pills */}
          <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '8px', padding: '3px', gap: '2px', flexShrink: 0 }}>
            {[{ v: '', l: 'Todos' }, { v: 'entrada', l: 'Entradas' }, { v: 'saida', l: 'Saídas' }].map(({ v, l }) => (
              <button
                key={v}
                onClick={() => handleFilterChange('type', v)}
                style={{
                  padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                  fontSize: '12px', fontWeight: '600', transition: 'all 0.15s',
                  background: filters.type === v ? '#fff' : 'transparent',
                  color: filters.type === v
                    ? (v === 'entrada' ? '#059669' : v === 'saida' ? '#dc2626' : '#1e293b')
                    : '#94a3b8',
                  boxShadow: filters.type === v ? '0 1px 3px rgba(0,0,0,0.10)' : 'none',
                }}
              >{l}</button>
            ))}
          </div>

          {/* Categoria */}
          <select
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            style={{
              padding: '7px 10px', borderRadius: '8px', border: '1px solid #e2e8f0',
              fontSize: '13px', color: filters.category ? '#1e293b' : '#94a3b8',
              background: '#f8fafc', outline: 'none', cursor: 'pointer', flexShrink: 0,
            }}
          >
            <option value="">Categoria</option>
            {filters.type && categories[filters.type] && categories[filters.type].map(cat => (
              <option key={`${filters.type}-${cat}`} value={cat}>{cat}</option>
            ))}
            {!filters.type && [
              ...categories.entrada.map(cat => ({ type: 'entrada', cat })),
              ...categories.saida.map(cat => ({ type: 'saida', cat }))
            ].map(({ type, cat }) => (
              <option key={`${type}-${cat}`} value={cat}>{cat}</option>
            ))}
          </select>

          {/* Mês */}
          <select
            value={filters.month}
            onChange={(e) => handleFilterChange('month', e.target.value)}
            style={{
              padding: '7px 10px', borderRadius: '8px', border: '1px solid #e2e8f0',
              fontSize: '13px', color: filters.month ? '#1e293b' : '#94a3b8',
              background: '#f8fafc', outline: 'none', cursor: 'pointer', flexShrink: 0,
            }}
          >
            <option value="">Mês</option>
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {format(new Date(2024, i, 1), 'MMMM', { locale: ptBR })}
              </option>
            ))}
          </select>

          {/* Limpar filtros */}
          {hasFiltersApplied() && (
            <button
              onClick={() => {
                const cleared = { ...filters, search: '', type: '', category: '', month: '' };
                setFilters(cleared);
                loadFinancialData(1, cleared);
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '6px 10px', borderRadius: '8px', border: '1px solid #fca5a5',
                background: '#fef2f2', color: '#dc2626', fontSize: '12px', fontWeight: '600',
                cursor: 'pointer', flexShrink: 0,
              }}
            >
              <X size={12} />
              Limpar
            </button>
          )}
        </div>
      </div>
      )}

      {/* Transactions Table - apenas na aba geral */}
      {activeTab === 'geral' && (
      <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', margin: 0 }}>Transações</h2>
            {!loading && pagination.total > 0 && (
              <span style={{
                fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '20px',
                background: '#eff6ff', color: '#3b82f6',
              }}>
                {pagination.total}
              </span>
            )}
          </div>
          {hasFiltersApplied() && !loading && (
            <span style={{ fontSize: '11px', color: '#64748b' }}>
              Filtrando resultados
            </span>
          )}
        </div>

        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px', gap: '10px' }}>
            <div className="loading" />
            <span style={{ fontSize: '14px', color: '#64748b' }}>Carregando...</span>
          </div>
        ) : transactions.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Data</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Descrição / Categoria</th>
                  <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Valor</th>
                  {canEdit && <th style={{ padding: '10px 20px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ações</th>}
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction, idx) => {
                  const isEntrada = transaction.type === 'entrada';
                  return (
                    <tr
                      key={transaction._id}
                      style={{
                        borderTop: idx === 0 ? 'none' : '1px solid #f1f5f9',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fafbfc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      {/* Data */}
                      <td style={{ padding: '12px 20px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{
                            width: '4px', height: '36px', borderRadius: '4px', flexShrink: 0,
                            background: isEntrada ? '#10b981' : '#ef4444',
                          }} />
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b', lineHeight: 1.2 }}>
                              {format(new Date(transaction.date), 'dd', { locale: ptBR })}
                            </div>
                            <div style={{ fontSize: '11px', color: '#94a3b8', lineHeight: 1.2, textTransform: 'capitalize' }}>
                              {format(new Date(transaction.date), 'MMM yyyy', { locale: ptBR })}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Descrição / Categoria */}
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: '13px', fontWeight: '500', color: '#1e293b' }}>
                          {transaction.description || transaction.category}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px', flexWrap: 'wrap' }}>
                          <span style={{
                            fontSize: '11px', fontWeight: '600', padding: '1px 7px', borderRadius: '20px',
                            background: isEntrada ? '#f0fdf4' : '#fff1f2',
                            color: isEntrada ? '#059669' : '#dc2626',
                          }}>
                            {transaction.category}
                          </span>
                          {transaction.tithePayer && (
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>· {transaction.tithePayer}</span>
                          )}
                        </div>
                      </td>

                      {/* Valor */}
                      <td style={{ padding: '12px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <span style={{
                          fontSize: '14px', fontWeight: '700',
                          color: isEntrada ? '#059669' : '#dc2626',
                        }}>
                          {isEntrada ? '+' : '-'} {formatCurrency(transaction.amount)}
                        </span>
                      </td>

                      {/* Ações */}
                      {canEdit && (
                        <td style={{ padding: '12px 20px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleEdit(transaction)}
                              title="Editar transação"
                              style={{
                                width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #e2e8f0',
                                background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', color: '#64748b',
                              }}
                            >
                              <Edit size={13} />
                            </button>
                            <button
                              onClick={() => handleDelete(transaction)}
                              title="Excluir transação"
                              style={{
                                width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #fee2e2',
                                background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', color: '#dc2626',
                              }}
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '56px 24px' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <FileText size={24} style={{ color: '#94a3b8' }} />
            </div>
            <p style={{ fontSize: '15px', fontWeight: '600', color: '#475569', margin: '0 0 4px' }}>Nenhuma transação encontrada</p>
            <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>
              {hasFiltersApplied() ? 'Tente ajustar os filtros' : 'Adicione a primeira transação clicando em "Nova Transação"'}
            </p>
          </div>
        )}
      </div>
      )}
       
       {/* Controles de Paginação */}
       {activeTab === 'geral' && !loading && transactions.length > 0 && (
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div className="text-sm text-secondary">
                Mostrando {((pagination.currentPage - 1) * pagination.limit) + 1} a {Math.min(pagination.currentPage * pagination.limit, pagination.total)} de {pagination.total} transações
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className="btn btn-sm btn-outline"
                >
                  Anterior
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNumber;
                    if (pagination.totalPages <= 5) {
                      pageNumber = i + 1;
                    } else if (pagination.currentPage <= 3) {
                      pageNumber = i + 1;
                    } else if (pagination.currentPage >= pagination.totalPages - 2) {
                      pageNumber = pagination.totalPages - 4 + i;
                    } else {
                      pageNumber = pagination.currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`btn btn-sm ${
                          pageNumber === pagination.currentPage
                            ? 'btn-primary'
                            : 'btn-outline'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="btn btn-sm btn-outline"
                >
                  Próximo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title={editingTransaction ? 'Editar Transação' : 'Nova Transação'}
        size="large"
        closeOnOverlayClick={false}
      >
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Tipo — toggle visual */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Tipo</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {[
                  { v: 'entrada', label: 'Entrada', icon: ArrowUpRight, color: '#059669', bg: '#f0fdf4', border: '#86efac', activeBg: '#dcfce7' },
                  { v: 'saida', label: 'Saída', icon: ArrowDownRight, color: '#dc2626', bg: '#fff1f2', border: '#fca5a5', activeBg: '#fee2e2' },
                ].map(({ v, label, icon: Icon, color, bg, border, activeBg }) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, type: v, category: '', tithePayer: '' }))}
                    style={{
                      padding: '14px', borderRadius: '12px', border: `2px solid`,
                      borderColor: formData.type === v ? color : '#e2e8f0',
                      background: formData.type === v ? activeBg : bg,
                      cursor: 'pointer', transition: 'all 0.15s',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                      fontWeight: '700', fontSize: '15px', color: formData.type === v ? color : '#94a3b8',
                    }}
                  >
                    <Icon size={18} />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Valor */}
            <div>
              <label htmlFor="transaction-amount" style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Valor</label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)',
                  fontSize: '15px', fontWeight: '700', color: formData.type === 'entrada' ? '#059669' : formData.type === 'saida' ? '#dc2626' : '#94a3b8',
                  pointerEvents: 'none',
                }}>R$</span>
                <input
                  id="transaction-amount"
                  type="text"
                  value={formData.amount}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^\d,\.]/g, '');
                    if (value.includes(',') && value.includes('.')) value = value.replace(/\./g, '');
                    if (value.includes('.') && !value.includes(',')) value = value.replace('.', ',');
                    const parts = value.split(',');
                    if (parts.length > 2) value = parts[0] + ',' + parts.slice(1).join('');
                    setFormData(prev => ({ ...prev, amount: value }));
                  }}
                  placeholder="0,00"
                  required
                  style={{
                    width: '100%', padding: '12px 14px 12px 44px', borderRadius: '10px',
                    border: '1.5px solid #e2e8f0', fontSize: '22px', fontWeight: '700',
                    color: formData.type === 'entrada' ? '#059669' : formData.type === 'saida' ? '#dc2626' : '#1e293b',
                    background: '#f8fafc', outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            {/* Categoria + Data lado a lado */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div>
                <label htmlFor="transaction-category" style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Categoria</label>
                <select
                  id="transaction-category"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value, tithePayer: e.target.value === 'Dízimos' ? prev.tithePayer : '' }))}
                  required
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '10px',
                    border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b',
                    background: '#f8fafc', outline: 'none', cursor: 'pointer', boxSizing: 'border-box',
                  }}
                >
                  <option value="">Selecionar...</option>
                  {categories[formData.type]?.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="transaction-date" style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Data</label>
                <input
                  id="transaction-date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  required
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '10px',
                    border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b',
                    background: '#f8fafc', outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            {/* Dizimista — condicional */}
            {formData.category === 'Dízimos' && (
              <div>
                <label htmlFor="transaction-tithe-payer" style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Nome do Dizimista</label>
                <input
                  id="transaction-tithe-payer"
                  type="text"
                  value={formData.tithePayer}
                  onChange={(e) => setFormData(prev => ({ ...prev, tithePayer: e.target.value }))}
                  required
                  placeholder="Nome completo do dizimista"
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '10px',
                    border: '1.5px solid #bfdbfe', fontSize: '14px', color: '#1e293b',
                    background: '#eff6ff', outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>
            )}

            {/* Descrição */}
            <div>
              <label htmlFor="transaction-description" style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>
                Descrição <span style={{ fontWeight: '400', textTransform: 'none', color: '#94a3b8' }}>(opcional)</span>
              </label>
              <input
                id="transaction-description"
                type="text"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ex: Oferta do culto de domingo"
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '10px',
                  border: '1.5px solid #e2e8f0', fontSize: '14px', color: '#1e293b',
                  background: '#f8fafc', outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          {/* Ações */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
            <button
              type="button"
              onClick={() => { setShowModal(false); resetForm(); }}
              style={{
                padding: '10px 20px', borderRadius: '10px', border: '1.5px solid #e2e8f0',
                background: '#fff', color: '#64748b', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              style={{
                padding: '10px 24px', borderRadius: '10px', border: 'none', cursor: 'pointer',
                fontSize: '14px', fontWeight: '700',
                background: formData.type === 'entrada' ? '#059669' : formData.type === 'saida' ? '#dc2626' : '#3b82f6',
                color: '#fff',
              }}
            >
              {editingTransaction ? 'Salvar alterações' : 'Criar transação'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Import Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={() => { setShowImportModal(false); setCsvFile(null); setImportErrors([]); setImportSuccess(null); }}
        title="Importar Transações CSV"
        size="lg"
        closeOnOverlayClick={false}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Info de formato */}
          <div style={{ background: '#f0f9ff', borderRadius: '12px', padding: '16px', border: '1px solid #bae6fd' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Info size={15} style={{ color: '#0369a1', flexShrink: 0 }} />
              <span style={{ fontSize: '13px', fontWeight: '700', color: '#0369a1' }}>Formato esperado das colunas</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {[
                { label: 'tipo', desc: 'entrada | saida' },
                { label: 'valor', desc: 'R$ 385,00' },
                { label: 'descricao', desc: 'texto livre' },
                { label: 'categoria', desc: 'nome da categoria' },
                { label: 'data', desc: 'dd/mm/aaaa' },
                { label: 'dizimista', desc: 'apenas Dízimos' },
              ].map(({ label, desc }) => (
                <div key={label} style={{
                  background: '#fff', borderRadius: '8px', padding: '6px 10px',
                  border: '1px solid #e0f2fe', fontSize: '12px',
                }}>
                  <span style={{ fontWeight: '700', color: '#0369a1' }}>{label}</span>
                  <span style={{ color: '#64748b', marginLeft: '4px' }}>— {desc}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Baixar modelo */}
          <button
            onClick={downloadTemplate}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              padding: '12px', borderRadius: '10px', border: '1.5px dashed #cbd5e1',
              background: '#f8fafc', color: '#475569', fontSize: '14px', fontWeight: '600',
              cursor: 'pointer', transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.color = '#3b82f6'; e.currentTarget.style.background = '#eff6ff'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#475569'; e.currentTarget.style.background = '#f8fafc'; }}
          >
            <Download size={16} />
            Baixar arquivo modelo (.csv)
          </button>

          {/* Upload */}
          <div>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '8px' }}>Selecionar arquivo CSV</label>
            <label style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: '8px', padding: '32px 20px', borderRadius: '12px',
              border: `2px dashed ${csvFile ? '#10b981' : '#cbd5e1'}`,
              background: csvFile ? '#f0fdf4' : '#f8fafc',
              cursor: 'pointer', transition: 'all 0.2s',
            }}>
              <input type="file" accept=".csv" onChange={(e) => { setCsvFile(e.target.files[0]); setImportErrors([]); setImportSuccess(null); }} style={{ display: 'none' }} />
              {csvFile ? (
                <>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Check size={20} style={{ color: '#059669' }} />
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#059669' }}>{csvFile.name}</span>
                  <span style={{ fontSize: '12px', color: '#6ee7b7' }}>Clique para trocar o arquivo</span>
                </>
              ) : (
                <>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Upload size={20} style={{ color: '#94a3b8' }} />
                  </div>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#475569' }}>Clique para selecionar</span>
                  <span style={{ fontSize: '12px', color: '#94a3b8' }}>Apenas arquivos .csv</span>
                </>
              )}
            </label>
          </div>

          {/* Feedback de sucesso */}
          {importSuccess && (
            <div style={{ background: '#f0fdf4', borderRadius: '10px', padding: '12px 14px', border: '1.5px solid #86efac', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Check size={16} style={{ color: '#059669', flexShrink: 0 }} />
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#059669' }}>{importSuccess}</span>
            </div>
          )}

          {/* Feedback de erros */}
          {importErrors.length > 0 && (
            <div style={{ background: '#fff1f2', borderRadius: '10px', border: '1.5px solid #fca5a5', overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px', borderBottom: importErrors.length > 1 ? '1px solid #fecaca' : 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertTriangle size={15} style={{ color: '#dc2626', flexShrink: 0 }} />
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#dc2626' }}>
                  {importErrors.length === 1 ? 'Problema encontrado' : `${importErrors.length} problemas encontrados`}
                </span>
              </div>
              {importErrors.length > 0 && (
                <ul style={{ margin: 0, padding: '8px 14px 10px 32px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {importErrors.map((err, i) => (
                    <li key={i} style={{ fontSize: '13px', color: '#b91c1c', lineHeight: '1.5' }}>{err}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Ações */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '4px' }}>
            <button
              type="button"
              onClick={() => { setShowImportModal(false); setCsvFile(null); setImportErrors([]); setImportSuccess(null); }}
              style={{
                padding: '10px 20px', borderRadius: '10px', border: '1.5px solid #e2e8f0',
                background: '#fff', color: '#64748b', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              onClick={() => handleCsvUpload(csvFile)}
              disabled={!csvFile || loading}
              style={{
                padding: '10px 24px', borderRadius: '10px', border: 'none', cursor: csvFile && !loading ? 'pointer' : 'not-allowed',
                fontSize: '14px', fontWeight: '700',
                background: csvFile && !loading ? '#3b82f6' : '#e2e8f0',
                color: csvFile && !loading ? '#fff' : '#94a3b8',
                transition: 'all 0.15s',
              }}
            >
              {loading ? 'Importando...' : 'Importar transações'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Tithers Modal */}
      <Modal
        isOpen={showTithersModal}
        onClose={() => setShowTithersModal(false)}
        title="Lista dos Dizimistas"
        size="lg"
        closeOnOverlayClick={false}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Stats bar */}
          {tithers.length > 0 && (() => {
            const currentDate = new Date();
            const currentYear = parseInt(filters.year) || currentDate.getFullYear();
            const currentYearActual = currentDate.getFullYear();
            const currentMonth = currentDate.getMonth();
            const monthsElapsed = currentYear < currentYearActual ? 12 : currentMonth;
            const regularCount = tithers.filter(t => {
              const months = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
              const paid = months.slice(0, monthsElapsed).filter(m => t.months[m] > 0).length;
              return monthsElapsed > 0 && paid === monthsElapsed;
            }).length;
            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '10px' }}>
                {[
                  { label: 'Dizimistas', value: tithers.length, color: '#3b82f6', bg: '#eff6ff' },
                  { label: 'Regulares', value: regularCount, color: '#059669', bg: '#f0fdf4' },
                  { label: 'Irregulares', value: tithers.length - regularCount, color: '#dc2626', bg: '#fef2f2' },
                ].map(({ label, value, color, bg }) => (
                  <div key={label} style={{ background: bg, borderRadius: '10px', padding: '12px 14px', textAlign: 'center' }}>
                    <div style={{ fontSize: '22px', fontWeight: '800', color }}>{value}</div>
                    <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', marginTop: '2px' }}>{label}</div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Legenda */}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {[
              { color: '#10b981', label: 'Dizimou' },
              { color: '#ef4444', label: 'Não Dizimou' },
              { color: '#cbd5e1', label: 'Pendente' },
            ].map(({ color, label }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: color }} />
                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>{label}</span>
              </div>
            ))}
          </div>

          {/* Tabela */}
          {tithers.length > 0 ? (
            <div style={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '700', color: '#64748b', whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0', position: 'sticky', left: 0, background: '#f8fafc', zIndex: 1 }}>Dizimista</th>
                    {['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'].map(m => (
                      <th key={m} style={{ padding: '10px 6px', textAlign: 'center', fontWeight: '700', color: '#94a3b8', borderBottom: '1px solid #e2e8f0', minWidth: '30px' }}>{m}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tithers.map((tithe, index) => {
                    const months = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
                    const currentDate = new Date();
                    const currentYear = parseInt(filters.year) || currentDate.getFullYear();
                    const currentMonth = currentDate.getMonth();
                    const currentYearActual = currentDate.getFullYear();
                    const paidCount = months.filter(m => tithe.months[m] > 0).length;
                    return (
                      <tr key={`tithe-${index}-${tithe.name}`} style={{ borderTop: index === 0 ? 'none' : '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 14px', fontWeight: '600', color: '#1e293b', whiteSpace: 'nowrap', position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                              width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                              background: `hsl(${(tithe.name || '').split('').reduce((a,c) => a + c.charCodeAt(0), 0) % 360}, 60%, 55%)`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '10px', fontWeight: '700', color: '#fff',
                            }}>
                              {(tithe.name || '?').trim().split(/\s+/).slice(0,2).map(p => p[0]).join('').toUpperCase()}
                            </div>
                            <div>
                              <div>{tithe.name}</div>
                              <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: '400' }}>{paidCount}/12 meses</div>
                            </div>
                          </div>
                        </td>
                        {months.map((month, monthIndex) => {
                          const monthPassed = currentYear < currentYearActual || (currentYear === currentYearActual && monthIndex < currentMonth);
                          const hasTithe = tithe.months[month] > 0;
                          const isCurrentMonth = currentYear === currentYearActual && monthIndex === currentMonth;
                          return (
                            <td key={`${index}-${month}`} style={{ padding: '10px 6px', textAlign: 'center' }}>
                              <div style={{
                                width: '18px', height: '18px', borderRadius: '4px', margin: '0 auto',
                                background: hasTithe ? '#10b981' : monthPassed ? '#ef4444' : '#e2e8f0',
                                border: isCurrentMonth ? '2px solid #3b82f6' : 'none',
                                title: hasTithe ? 'Dizimou' : monthPassed ? 'Não Dizimou' : 'Pendente',
                              }} title={hasTithe ? `${tithe.name} - dizimou` : monthPassed ? `${tithe.name} - não dizimou` : 'Pendente'} />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '48px 24px' }}>
              <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <Users size={24} style={{ color: '#94a3b8' }} />
              </div>
              <p style={{ fontSize: '15px', fontWeight: '600', color: '#475569', margin: '0 0 4px' }}>Nenhum dizimista encontrado</p>
              <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>Os dizimistas aparecem automaticamente ao registrar dízimos</p>
            </div>
          )}

          {/* Ações */}
          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '16px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {['administracao','administrador','tesoureiro'].includes(String(user?.role || '').toLowerCase()) && (
                <button
                  type="button"
                  onClick={purgeInactiveTithers}
                  disabled={tithersPurgeLoading}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
                    border: '1.5px solid #fca5a5', background: '#fef2f2', color: '#dc2626',
                    cursor: tithersPurgeLoading ? 'not-allowed' : 'pointer',
                    opacity: tithersPurgeLoading ? 0.6 : 1,
                  }}
                >
                  <Trash2 size={13} />
                  {tithersPurgeLoading ? 'Removendo...' : 'Remover inativos (3 meses)'}
                </button>
              )}
              <button
                type="button"
                onClick={loadTithersArchive}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
                  border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#475569', cursor: 'pointer',
                }}
              >
                Histórico de removidos
              </button>
            </div>
            {tithersPurgeResult && (
              <span style={{ fontSize: '12px', color: '#94a3b8' }} aria-live="polite">
                Removidos recentemente: {tithersPurgeResult.count}
              </span>
            )}
            <button
              type="button"
              onClick={() => setShowTithersModal(false)}
              style={{
                padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e2e8f0',
                background: '#fff', color: '#64748b', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
              }}
            >
              Fechar
            </button>
          </div>

          {/* Histórico de removidos */}
          {tithersArchived.length > 0 && (
            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '10px' }}>Histórico de removidos</div>
              <div style={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: '700', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>Nome</th>
                      <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: '700', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>Última contribuição</th>
                      <th style={{ padding: '9px 14px', textAlign: 'left', fontWeight: '700', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>Motivo</th>
                      <th style={{ padding: '9px 14px', borderBottom: '1px solid #e2e8f0' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tithersArchived.map((a, i) => (
                      <tr key={`arch-${i}`} style={{ borderTop: i === 0 ? 'none' : '1px solid #f1f5f9' }}>
                        <td style={{ padding: '10px 14px', fontWeight: '600', color: '#1e293b' }}>{a.name}</td>
                        <td style={{ padding: '10px 14px', color: '#64748b' }}>{a.lastContributionDate ? format(new Date(a.lastContributionDate), 'dd/MM/yyyy') : '-'}</td>
                        <td style={{ padding: '10px 14px', color: '#64748b' }}>{a.reason || '-'}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                          {['administracao','administrador','tesoureiro'].includes(String(user?.role || '').toLowerCase()) && (
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  await api.post('/financial/tithers/activate', { name: a.name });
                                  setTithersArchived(prev => prev.filter(x => x.name !== a.name));
                                  toast.success('Dizimista ativado');
                                  await loadTithers();
                                } catch (e) {
                                  toast.error(e?.response?.data?.message || 'Falha ao ativar dizimista');
                                }
                              }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '5px',
                                padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '600',
                                border: '1.5px solid #6ee7b7', background: '#f0fdf4', color: '#059669', cursor: 'pointer',
                              }}
                            >
                              <Check size={12} />
                              Ativar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                type="button"
                onClick={() => setTithersArchived([])}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px', marginTop: '10px',
                  padding: '7px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '600',
                  border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#64748b', cursor: 'pointer',
                }}
              >
                <ArrowLeft size={12} />
                Voltar para lista
              </button>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={showMatchesModal}
        onClose={() => setShowMatchesModal(false)}
        title=""
        size="lg"
        closeOnOverlayClick={false}
      >
        <div style={{ margin: '-24px -24px 0', borderRadius: '12px 12px 0 0', overflow: 'hidden' }}>
          {/* Header gradiente */}
          <div style={{
            background: 'linear-gradient(135deg, #6d28d9 0%, #7c3aed 60%, #a78bfa 100%)',
            padding: '20px 24px 16px',
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', width: '140px', height: '140px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', top: '-50px', right: '-30px' }} />
            <div style={{ position: 'absolute', width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', bottom: '-20px', right: '80px' }} />
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
                  <Users style={{ width: '20px', height: '20px', color: '#fff' }} />
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#fff' }}>Matches de Dizimistas</h2>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.75)' }}>
                    {matchesDataset === 'banco-banco' ? 'Duplicidades no banco de dados' : 'Nomes não vinculados ao cadastro'}
                  </p>
                </div>
              </div>
              {matchesPagination?.total > 0 && (
                <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '20px', padding: '4px 12px', backdropFilter: 'blur(4px)' }}>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: '#fff' }}>{matchesPagination.total} pendente{matchesPagination.total !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          </div>

          {/* Barra de controles */}
          <div style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {/* Dataset toggle */}
            <div style={{ display: 'flex', background: '#e2e8f0', borderRadius: '8px', padding: '2px', gap: '2px' }}>
              {[{ v: 'banco-cadastro', label: 'Banco × Cadastro' }, { v: 'banco-banco', label: 'Banco × Banco' }].map(opt => (
                <button
                  key={opt.v}
                  onClick={() => setMatchesDataset(opt.v)}
                  style={{
                    padding: '5px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600', transition: 'all .15s',
                    background: matchesDataset === opt.v ? '#fff' : 'transparent',
                    color: matchesDataset === opt.v ? '#6d28d9' : '#64748b',
                    boxShadow: matchesDataset === opt.v ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  }}
                >{opt.label}</button>
              ))}
            </div>
            {/* Filtros */}
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '12px', color: '#475569', userSelect: 'none' }}>
              <input type="checkbox" checked={matchesPendingOnly} onChange={e => setMatchesPendingOnly(e.target.checked)} style={{ accentColor: '#7c3aed' }} />
              Só pendentes
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '12px', color: '#475569', userSelect: 'none' }}>
              <input type="checkbox" checked={matchesReviewOnly} onChange={e => setMatchesReviewOnly(e.target.checked)} style={{ accentColor: '#7c3aed' }} />
              Só revisão (60–80)
            </label>
            <button
              onClick={loadMatches}
              disabled={matchesLoading}
              style={{
                marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 14px', borderRadius: '8px', border: '1.5px solid #7c3aed',
                background: matchesLoading ? '#f3f4f6' : '#fff', color: '#7c3aed',
                fontSize: '12px', fontWeight: '600', cursor: matchesLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {matchesLoading
                ? <><span style={{ width: '12px', height: '12px', border: '2px solid #a78bfa', borderTopColor: '#7c3aed', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} /> Carregando…</>
                : '↺ Atualizar'
              }
            </button>
          </div>
        </div>

        {/* Corpo */}
        <div style={{ padding: '16px 0 0' }}>
          {/* Stats strip */}
          {matchesStats && (
            <div style={{ display: 'flex', gap: '16px', padding: '0 0 12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', borderRadius: '6px', padding: '3px 8px' }}>
                Precisão mínima: <strong>{matchesStats.threshold}%</strong>
              </span>
              {matchesDataset === 'banco-cadastro' && (
                <span style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', borderRadius: '6px', padding: '3px 8px' }}>
                  Nomes no banco: <strong>{matchesStats.txDistinctNames}</strong>
                </span>
              )}
              {matchesDataset === 'banco-banco' && (
                <span style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', borderRadius: '6px', padding: '3px 8px' }}>
                  Nomes distintos: <strong>{matchesStats.totalNames}</strong>
                </span>
              )}
            </div>
          )}

          {/* Loading state */}
          {matchesLoading && matches.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <div style={{ width: '36px', height: '36px', border: '3px solid #e9d5ff', borderTopColor: '#7c3aed', borderRadius: '50%', margin: '0 auto 12px', animation: 'spin 0.7s linear infinite' }} />
              <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>Analisando nomes…</p>
            </div>
          )}

          {/* Empty state */}
          {matches.length === 0 && !matchesLoading && (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <Check style={{ width: '28px', height: '28px', color: '#22c55e' }} />
              </div>
              <p style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', margin: '0 0 4px' }}>Tudo em ordem!</p>
              <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>Nenhum match pendente encontrado para os filtros selecionados.</p>
            </div>
          )}

          {/* Cards de match */}
          {matches.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {matches.map((m, idx) => {
                const score = m.finalScore || 0;
                const scoreColor = score >= 85 ? '#16a34a' : score >= 70 ? '#d97706' : '#64748b';
                const scoreBg = score >= 85 ? '#f0fdf4' : score >= 70 ? '#fffbeb' : '#f8fafc';
                const scoreBorder = score >= 85 ? '#bbf7d0' : score >= 70 ? '#fde68a' : '#e2e8f0';
                return (
                  <div key={`match-${idx}`} style={{
                    border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px 16px',
                    background: '#fff', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
                    transition: 'box-shadow .15s',
                  }}>
                    {/* Score badge */}
                    <div style={{
                      minWidth: '52px', textAlign: 'center', padding: '6px 10px',
                      background: scoreBg, border: `1.5px solid ${scoreBorder}`, borderRadius: '10px', flexShrink: 0,
                    }}>
                      <div style={{ fontSize: '16px', fontWeight: '800', color: scoreColor, lineHeight: 1 }}>{score}</div>
                      <div style={{ fontSize: '9px', fontWeight: '600', color: scoreColor, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>score</div>
                    </div>

                    {/* Nomes */}
                    <div style={{ flex: 1, minWidth: '160px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', background: '#f1f5f9', borderRadius: '6px', padding: '2px 8px' }}>{m.dbName}</span>
                        <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600' }}>↔</span>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', background: '#f1f5f9', borderRadius: '6px', padding: '2px 8px' }}>{m.inputName}</span>
                      </div>
                      {m.lastTransaction && (
                        <div style={{ marginTop: '5px', fontSize: '11px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>Último dízimo: <strong style={{ color: '#475569' }}>
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(m.lastTransaction.amount || 0)}
                          </strong></span>
                          <span>·</span>
                          <span>{m.lastTransaction.date ? format(new Date(m.lastTransaction.date), 'dd/MM/yyyy') : '-'}</span>
                        </div>
                      )}
                    </div>

                    {/* Ações */}
                    <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                      <button
                        onClick={() => { setPendingUnify(m); setShowConfirmUnify(true); }}
                        disabled={matchesLoading}
                        style={{
                          padding: '7px 14px', borderRadius: '8px', border: 'none', cursor: matchesLoading ? 'not-allowed' : 'pointer',
                          background: '#16a34a', color: '#fff', fontSize: '12px', fontWeight: '600',
                          display: 'flex', alignItems: 'center', gap: '4px',
                        }}
                      >
                        <Check style={{ width: '12px', height: '12px' }} /> Unificar
                      </button>
                      <button
                        onClick={() => ignoreMatch(m)}
                        disabled={matchesLoading}
                        style={{
                          padding: '7px 14px', borderRadius: '8px', border: '1.5px solid #fca5a5', cursor: matchesLoading ? 'not-allowed' : 'pointer',
                          background: '#fff5f5', color: '#ef4444', fontSize: '12px', fontWeight: '600',
                          display: 'flex', alignItems: 'center', gap: '4px',
                        }}
                      >
                        <X style={{ width: '12px', height: '12px' }} /> Rejeitar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Paginação */}
          {matchesPagination && matchesPagination.totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
              <button
                disabled={matchesPage === 1 || matchesLoading}
                onClick={() => { setMatchesPage(Math.max(1, matchesPage - 1)); setTimeout(loadMatches, 0); }}
                style={{ padding: '6px 14px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: '12px', fontWeight: '600', cursor: (matchesPage === 1 || matchesLoading) ? 'not-allowed' : 'pointer', opacity: (matchesPage === 1 || matchesLoading) ? 0.5 : 1 }}
              >← Anterior</button>
              <span style={{ fontSize: '12px', color: '#64748b' }}>Página <strong>{matchesPage}</strong> de <strong>{matchesPagination.totalPages}</strong></span>
              <button
                disabled={matchesPage >= matchesPagination.totalPages || matchesLoading}
                onClick={() => { setMatchesPage(Math.min(matchesPagination.totalPages, matchesPage + 1)); setTimeout(loadMatches, 0); }}
                style={{ padding: '6px 14px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: '12px', fontWeight: '600', cursor: (matchesPage >= matchesPagination.totalPages || matchesLoading) ? 'not-allowed' : 'pointer', opacity: (matchesPage >= matchesPagination.totalPages || matchesLoading) ? 0.5 : 1 }}
              >Próximo →</button>
            </div>
          )}
          {matchesPagination && matchesPagination.total > 0 && matches.length > 0 && (
            <p style={{ textAlign: 'center', fontSize: '11px', color: '#94a3b8', marginTop: '8px' }}>
              Exibindo {matches.length} de {matchesPagination.total} registros
            </p>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
          <button
            type="button"
            onClick={() => setShowMatchesModal(false)}
            style={{ padding: '8px 20px', borderRadius: '8px', border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
          >
            Fechar
          </button>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={showConfirmUnify}
        onClose={() => { setShowConfirmUnify(false); setPendingUnify(null); }}
        onConfirm={async () => { if (pendingUnify) { await unifyMatch(pendingUnify); } setShowConfirmUnify(false); setPendingUnify(null); }}
        title="Confirmar Unificação"
        message={(function(){ const plan = getUnifyPlan(pendingUnify); return plan.toName ? `Tem certeza que deseja unificar "${(plan.fromNames||[]).join(', ')}" em "${plan.toName}"? Esta ação atualizará todas as transações correspondentes.` : 'Confirmar a unificação selecionada.'; })()}
        confirmText="Unificar"
        cancelText="Cancelar"
        confirmVariant="success"
      />

      {/* Modal de Formulário de Despesas Fixas */}
      <FixedExpenseForm
        isOpen={showFixedExpenseForm}
        onClose={handleCloseFixedExpenseForm}
        onSubmit={handleFixedExpenseSubmit}
        expense={editingFixedExpense}
        loading={fixedExpensesLoading}
      />

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setTransactionToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir a transação ${transactionToDelete?.description ? `"${transactionToDelete.description}"` : 'selecionada'}? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        confirmVariant="danger"
      />

      <ConfirmModal
        isOpen={!!expenseToDelete}
        onClose={() => setExpenseToDelete(null)}
        onConfirm={confirmDeleteFixedExpense}
        title="Excluir Despesa Fixa"
        message={`Tem certeza que deseja excluir a despesa "${expenseToDelete?.name}"?`}
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="danger"
      />
    </div>
   );
 };

export default Financial;
